import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';

import { BreadcrumbLink, BreadcrumbPage } from '@/components/ui/breadcrumb';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type PageBreadcrumbDropdownItem = {
    label: string;
    to: string;
};

export type PageBreadcrumbDropdown = {
    title?: string;
    onOpenChange?: (open: boolean) => void;
    isLoading?: boolean;
    items?: PageBreadcrumbDropdownItem[];
    emptyLabel?: string;
};

type Props = {
    label: React.ReactNode;
    kicker?: string;
    to?: string;
    isCurrent?: boolean;
    dropdown?: PageBreadcrumbDropdown | PageBreadcrumbDropdown[];
    /** Hint to PageBreadcrumb on which separator to render before this segment. */
    separator?: 'chevron' | 'arrow';
    dataTest?: string;
};

const labelClasses = 'flex flex-col leading-tight';
const kickerClasses = 'text-[10px] uppercase tracking-wider text-muted-foreground/80';

const withKicker = (kicker: string | undefined, node: React.ReactNode) =>
    kicker ? (
        <span className={labelClasses}>
            <span className={kickerClasses}>{kicker}</span>
            {node}
        </span>
    ) : (
        node
    );

export const PageBreadcrumbSegment = ({ label, kicker, to, isCurrent, dropdown, dataTest }: Props) => {
    // `separator` is intentionally consumed by the parent <PageBreadcrumb>, not here.
    const labelNode = isCurrent ? (
        <BreadcrumbPage className="font-medium" data-test={dataTest}>
            {withKicker(kicker, label)}
        </BreadcrumbPage>
    ) : to ? (
        <BreadcrumbLink asChild>
            <Link to={to} data-test={dataTest}>
                {withKicker(kicker, label)}
            </Link>
        </BreadcrumbLink>
    ) : (
        <span className="text-muted-foreground" data-test={dataTest}>
            {withKicker(kicker, label)}
        </span>
    );

    if (!dropdown) {
        return labelNode;
    }

    const sections = Array.isArray(dropdown) ? dropdown : [dropdown];
    const onOpenChange = sections.find((section) => section.onOpenChange)?.onOpenChange;

    return (
        <div className="inline-flex items-center gap-0.5">
            {labelNode}
            <DropdownMenu onOpenChange={onOpenChange}>
                <DropdownMenuTrigger
                    className={cn(
                        'inline-flex size-5 items-center justify-center rounded text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                    aria-label="Ouvrir le menu"
                    data-test={dataTest ? `${dataTest}-dropdown-trigger` : undefined}
                >
                    <ChevronDown className="size-4" strokeWidth={1.5} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-48">
                    {sections.map((section, index) => (
                        <React.Fragment key={index}>
                            {index > 0 ? <DropdownMenuSeparator /> : null}
                            {section.title ? <DropdownMenuLabel>{section.title}</DropdownMenuLabel> : null}
                            {section.isLoading ? (
                                <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
                                    <Loader2 className="size-3.5 animate-spin" />
                                    Chargement…
                                </div>
                            ) : !section.items || section.items.length === 0 ? (
                                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                    {section.emptyLabel ?? 'Aucun élément'}
                                </div>
                            ) : (
                                section.items.map((item) => (
                                    <DropdownMenuItem key={item.to} asChild>
                                        <Link to={item.to}>{item.label}</Link>
                                    </DropdownMenuItem>
                                ))
                            )}
                        </React.Fragment>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};
